import React from 'react';
import * as Sentry from '@sentry/react';
import i18n from '../i18n';
import { isChunkLoadError, reloadForChunkError } from '../utils/chunkReload';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, recovering: false };
    }

    static getDerivedStateFromError(error) {
        // A chunk-load failure is an EXPECTED post-deploy condition (the open tab
        // referenced the previous build's chunks), not a crash. Render a quiet
        // "updating" state instead of the error page while we reload.
        return { hasError: true, error, recovering: isChunkLoadError(error) };
    }

    componentDidCatch(error, errorInfo) {
        if (isChunkLoadError(error)) {
            // Recover silently by reloading once. If the loop guard suppresses the
            // reload (we already reloaded and it STILL fails → a genuinely broken
            // chunk, not just a stale one), fall back to the visible error page
            // rather than spin on the "updating" screen forever. Don't report these
            // to Sentry — they're routine deploys, not errors.
            const reloading = reloadForChunkError();
            if (!reloading) this.setState({ recovering: false });
            return;
        }
        console.error('Error caught by boundary:', error, errorInfo);
        Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    }

    render() {
        if (this.state.hasError) {
            if (this.state.recovering) {
                // Minimal full-screen "updating to the latest version" state shown
                // while the reload triggered above is in flight — no scary red page.
                return (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: '#f7fafc',
                        gap: '16px'
                    }}>
                        <style>{'@keyframes mm-spin{to{transform:rotate(360deg)}}'}</style>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '3px solid #cbd5e1',
                            borderTopColor: '#3b82f6',
                            borderRadius: '50%',
                            animation: 'mm-spin 0.8s linear infinite'
                        }} />
                        <p style={{ color: '#4a5568', margin: 0 }}>{i18n.t('cm.errorBoundary.updating')}</p>
                    </div>
                );
            }
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#f7fafc'
                }}>
                    <div style={{
                        maxWidth: '500px',
                        backgroundColor: 'white',
                        padding: '40px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                    }}>
                        <h1 style={{ color: '#e53e3e', marginBottom: '16px' }}>{i18n.t('cm.errorBoundary.title')}</h1>
                        <p style={{ color: '#4a5568', marginBottom: '24px' }}>
                            {i18n.t('cm.errorBoundary.message')}
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                padding: '12px 24px',
                                borderRadius: '6px',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px'
                            }}
                        >
                            {i18n.t('cm.errorBoundary.reload')}
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
